import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Tooltip from '@/ui/components/Tooltip';

// Shared task-row cell used across Active, Unacked, and Recent panels so the
// Task column in each view looks the same: task name + id + truncated args +
// kwargs stacked, with optional error in red. args/kwargs can be very long
// (UUID lists, big payloads), so we always truncate here — the expanded
// detail row on Recent shows the full value alongside a copy button.

const PREVIEW_CHARS = 80;

const truncate = (value: string | null | undefined): string | null => {
  if (!value) return null;
  return value.length > PREVIEW_CHARS ? `${value.slice(0, PREVIEW_CHARS)}…` : value;
};

// Matches the backend dashboard.templatetags.homebase_helpers.datadog_url
// pattern (@key:value tagged search) so the URLs the console emits are the
// same shape as person.datadog_url and other server-side links.
const datadogLogsUrl = (taskId: string): string =>
  `https://app.datadoghq.com/logs?query=${encodeURIComponent(`@task_id:${taskId}`)}`;

const TaskCell = ({
  name,
  id,
  args,
  kwargs,
  error
}: {
  name: string | null;
  id: string | null;
  args?: string | null;
  kwargs?: string | null;
  error?: string | null;
}) => {
  const argsPreview = truncate(args);
  const kwargsPreview = truncate(kwargs);
  const errorPreview = truncate(error);
  return (
    <td className="py-2 font-mono align-top">
      <div>{name ?? '—'}</div>
      {id && (
        <div className="text-xs text-gray-400 flex items-center gap-1">
          {id}
          <Tooltip tooltipContent="Open Datadog logs for this task id">
            <a
              href={datadogLogsUrl(id)}
              target="_blank"
              rel="noreferrer"
              className="hover:text-gray-700"
              onClick={(event) => event.stopPropagation()}>
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </a>
          </Tooltip>
        </div>
      )}
      {argsPreview && (
        <div className="text-xs text-gray-500 wrap-break-word">args: {argsPreview}</div>
      )}
      {kwargsPreview && (
        <div className="text-xs text-gray-500 wrap-break-word">kwargs: {kwargsPreview}</div>
      )}
      {errorPreview && (
        <div className="text-xs text-red-600 wrap-break-word">error: {errorPreview}</div>
      )}
    </td>
  );
};

export default TaskCell;
